import { Component, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import {
  FlashCardStacksService,
  FlashCardsService,
} from 'src/app/api/services';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ImportCsvErrorDialogComponent } from 'src/app/cms/common/dialogs/import-csv-error-dialog.component';
import { CsvImportProgressDialogComponent } from 'src/app/cms/common/dialogs/csv-import-progress-dialog.component';
import { CancelDialogLabels } from 'src/app/cms/common/dialogs/cancel-confirmation.component';
import { MatList, MatListItem } from '@angular/material/list';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { CsvImportUploadAreaComponent } from '../../../common/csv-import-upload-area.component';
import { MatIcon } from '@angular/material/icon';
import { ContentSpinnerComponent } from '../../../common/ui/content-spinner.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { LIST_ITEM_HEIGHT, validateSchema } from 'src/app/utility/app.utils';
import { FlashCardEditComponent } from '../../../edit/flash-card-edit.component';
import { schemas } from 'src/app/payload-validators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgScrollbar } from 'ngx-scrollbar';

@Component({
  selector: 'qa-import-flash-card-csv',
  templateUrl: './import-flash-card-csv.component.html',
  styleUrls: ['./import-flash-card-csv.component.scss'],
  imports: [
    CsvImportUploadAreaComponent,
    MatIcon,
    ContentSpinnerComponent,
    FormsModule,
    NgTemplateOutlet,
    NgClass,
    MatCheckbox,
    MatButton,
    ButtonComponent,
    TranslateModule,
    MatList,
    MatListItem,
    NgScrollbar,
    FlashCardEditComponent,
  ],
})
export class ImportFlashCardCsvComponent {
  @ViewChild(FlashCardEditComponent) flashCardEdit: FlashCardEditComponent;
  id: string;
  courseId: string;
  bookId: string;
  data;
  loading = false;
  navigateForm = true;
  removedItemIdx: number[] = [];
  currentItemIndex = 0;
  isChecked = true;
  showOverview = false;
  successFlashCard = [];
  failedFlashCard = [];
  isComplete = false;
  inProgress = false;
  uploadAreaParameters = {
    csv: 'assets/import-csv-templates/Sample-flash-card-import.csv',
    excel: 'assets/import-csv-templates/Sample-flash-card-import.xlsx',
    csvError: false,
  };
  dialogRef: MatDialogRef<CsvImportProgressDialogComponent>;
  cancelUploadLabels: CancelDialogLabels = {
    messages: [
      this.translate.instant('csvImports.cancelConfirmation.message1'),
      this.translate.instant('csvImports.cancelConfirmation.message2'),
    ],
    buttonLabels: {
      confirm: this.translate.instant('common.button.continue'),
      cancel: this.translate.instant('Quit'),
    },
  };

  LIST_ITEM_HEIGHT = LIST_ITEM_HEIGHT;

  constructor(
    private cdRef: ChangeDetectorRef,
    private flashCardStacks: FlashCardStacksService,
    private flashCardService: FlashCardsService,
    private matDialog: MatDialog,
    private translate: TranslateService,
    private matSnackBar: MatSnackBar,
    private router: Router,
    route: ActivatedRoute
  ) {
    route.params.subscribe((params) => {
      if (params.id) {
        this.id = params.id;
      }
    });
    route.queryParams.subscribe((params) => {
      if (params.course) {
        this.courseId = params.course;
      }
      if (params.book) {
        this.bookId = params.book;
      }
    });
  }

  importCsv() {
    const sentryInfo = {
      title: `Error while importing stack cards from CSV`,
      file: 'import-flash-card-csv.component.ts - import from CSV',
      api: 'addFlashCards',
      preventLogs: true,
    };
    const validateCardsPayload = validateSchema(
      schemas.FlashCardModel.array(),
      this.data.csvData,
      this.matSnackBar,
      sentryInfo
    );
    if (validateCardsPayload?.isInvalidSchema) {
      return;
    }
    this.dialogRef = this.matDialog.open(CsvImportProgressDialogComponent, {
      disableClose: true,
      data: this.getModalData(false),
    });
    this.addFlashCards(0);
    this.dialogRef.afterClosed().subscribe(() => {
      this.backToList();
    });
  }

  uploadCsv(tableData) {
    try {
      const arr = tableData
        .map((row) => {
          return Object.keys(row).reduce((prev, cur) => {
            if (cur === 'number') {
              return prev;
            }
            prev[cur.toLowerCase()] = row[cur];
            return prev;
          }, {});
        })
        .map((data) => {
          data['tags'] = data['tags']
            ? data['tags'].split(':').map((tag) => ({ name: tag.trim() }))
            : [];
          if (data['media_id'] && data['media_url']) {
            data['media'] = {
              id: data['media_id'],
              url: data['media_url'],
              file_ext: data['fileextension'],
              uuid: data['uuid'],
            };
          }
          delete data['media_id'];
          delete data['media_url'];
          delete data['fileextension'];
          delete data['uuid'];
          return data;
        });
      this.data = {
        csvData: arr,
        parentId: this.id,
        navigationLabels: {
          prev: this.translate.instant(
            'csvImports.flashCard.navigationLabel.prev'
          ),
          next: this.translate.instant(
            'csvImports.flashCard.navigationLabel.next'
          ),
        },
      };
      let errinfo = null;
      if (!arr || arr.length === 0) {
        this.data = undefined;
        errinfo = {
          code: 0,
        };
      } else {
        let line = 0;
        for (const card of this.data.csvData) {
          line++;
          if (!card.text || !card.answer) {
            this.data = undefined;
            errinfo = {
              code: 1,
              pos: line,
            };
            break;
          }
        }
      }
      if (!this.data) {
        this.matDialog
          .open(ImportCsvErrorDialogComponent, {
            data: {
              text: this.translate.instant(
                'common.message.csvUploadError.textFlashCard'
              ),
              templateUrl: {
                link1: {
                  title: this.translate.instant(
                    'common.message.csvUploadError.excelCSV'
                  ),
                  href: 'https://quizacademy.de/tipps/excel-csv-inhalte-importieren/',
                },
                link2: {
                  title: this.translate.instant(
                    'common.message.csvUploadError.quizlet'
                  ),
                  href: 'https://quizacademy.de/tipps/quizlet-inhalte-importieren/',
                },
              },
              error: errinfo,
            },
          })
          .afterClosed()
          .subscribe(() => {
            this.uploadAreaParameters.csvError = true;
            this.uploadAreaParameters = Object.assign(
              {},
              this.uploadAreaParameters
            );
          });
        return;
      }
      this.removedItemIdx = [];
      this.currentItemIndex = 0;
      this.isChecked = true;
      this.loading = false;
    } catch (error) {
      console.log(error);
    }
  }

  backToList() {
    if (this.id) {
      if (this.courseId) {
        this.router.navigate([
          `/cms/courses/edit/${this.courseId}`,
          { course: this.courseId, 'flash-card-stack': this.id },
        ]);
      } else if (this.bookId) {
        this.router.navigate([
          `/cms/books/edit/${this.bookId}`,
          { book: this.bookId, 'flash-card-stack': this.id },
        ]);
      } else {
        this.router.navigate([`/cms/content/${this.id}/flash-cards`]);
      }
    } else {
      this.router.navigate([`/cms/content/flash-cards`]);
    }
  }

  onNavigate(currentFlashCardIndex: number) {
    if (currentFlashCardIndex < this.data.csvData.length) {
      this.navigateForm = false;
      this.isChecked = this.removedItemIdx.includes(currentFlashCardIndex)
        ? false
        : true;
      setTimeout(() => {
        this.currentItemIndex = currentFlashCardIndex;
        this.navigateForm = true;
      }, 10);
      this.cdRef.detectChanges();
    } else {
      this.showOverview = true;
    }
  }

  onChange(event, index?: number) {
    if (index === this.currentItemIndex && (index || index === 0)) {
      this.isChecked = event.checked;
    }
    if (event.checked) {
      const remIdx = index || index === 0 ? index : this.currentItemIndex;
      const idx = this.removedItemIdx.findIndex((x) => x === remIdx);
      this.removedItemIdx.splice(idx, 1);
    } else {
      const idx = index || index === 0 ? index : this.currentItemIndex;
      if (!this.removedItemIdx.includes(idx)) {
        this.removedItemIdx.push(idx);
      }
    }
  }

  onSelectAll() {
    if (!this.isComplete) {
      if (this.removedItemIdx.length) {
        this.removedItemIdx = [];
      } else {
        for (
          let eachCsvData = 0;
          eachCsvData < this.data.csvData.length;
          eachCsvData++
        ) {
          this.removedItemIdx.push(eachCsvData);
        }
      }
    }
  }

  addFlashCards(idx: number) {
    if (!this.removedItemIdx.includes(idx)) {
      if (this.id) {
        this.flashCardStacks
          .copyCardsToFlashCardStack({
            id: this.data.parentId,
            body: [this.data.csvData[idx]],
          })
          .subscribe(
            () => {
              this.successFlashCard.push(idx);
              if (this.dialogRef && this.dialogRef.componentInstance) {
                this.dialogRef.componentInstance.data =
                  this.getModalData(false);
              }
              this.handleApiCalls(idx);
            },
            () => {
              this.failedFlashCard.push(idx);
              if (this.dialogRef && this.dialogRef.componentInstance) {
                this.dialogRef.componentInstance.data =
                  this.getModalData(false);
              }
              this.handleApiCalls(idx);
            }
          );
      } else {
        this.flashCardService
          .createFlashCard({
            body: this.data.csvData[idx],
          })
          .subscribe(
            () => {
              this.successFlashCard.push(idx);
              if (this.dialogRef && this.dialogRef.componentInstance) {
                this.dialogRef.componentInstance.data =
                  this.getModalData(false);
              }
              this.handleApiCalls(idx);
            },
            () => {
              this.failedFlashCard.push(idx);
              if (this.dialogRef && this.dialogRef.componentInstance) {
                this.dialogRef.componentInstance.data =
                  this.getModalData(false);
              }
              this.handleApiCalls(idx);
            }
          );
      }
    } else {
      this.handleApiCalls(idx);
    }
  }

  handleApiCalls(idx: number) {
    idx++;
    if (idx < this.data.csvData.length) {
      this.addFlashCards(idx);
    } else {
      setTimeout(() => {
        this.isComplete = true;
        this.inProgress = false;
        if (this.dialogRef && this.dialogRef.componentInstance) {
          this.dialogRef.componentInstance.data = this.getModalData(true);
        }
      }, 500);
    }
  }

  getModalData(isComplete: boolean) {
    return {
      total: this.data.csvData.length - this.removedItemIdx.length,
      success: this.successFlashCard.length,
      failed: this.failedFlashCard.length,
      isProgress: !isComplete,
      isComplete,
      label: {
        progress: 'Importing Flashcard',
        success: this.translate.instant(
          'content.flashCard.csvUpload.successUploadMessage'
        ),
      },
    };
  }

  overviewData() {
    this.showOverview = true;
  }

  back() {
    this.showOverview = false;
    this.isChecked = this.removedItemIdx.includes(this.currentItemIndex)
      ? false
      : true;
  }

  editCard(index: number) {
    this.currentItemIndex = index;
    this.isChecked = this.removedItemIdx.includes(index) ? false : true;
    this.back();
  }

  cancel() {
    if (this.isComplete) {
      this.backToList();
    } else {
      this.data = undefined;
      this.showOverview = false;
    }
  }

  onNext(event: string) {
    if (event) {
      this.uploadCsv(event);
    }
  }

  onCancel(event) {
    if (event) {
      this.backToList();
    }
  }
}
